import React, { useContext, useEffect, useRef, type FC } from 'react';
import {
  CategoryID,
  FeatureURI,
  type AdItem as AdItemType,
} from '@sbt-web/network/types';
import qualitySeals from '@client/components/Detail/QualitySeal/quality-seals';
import type { RootState } from '@reducers/index';
import ItemCard from '@sbt-web/item-card';
import { getMacroOfCategoryID } from '@sbt-web/category-utils';
import { HADES_PATH } from '@shared/constants';
import {
  buildCardClickTrackingData,
  buildCardImpressionTrackingViewData,
  getTrackingItem,
} from '@tools/tracking/adsListingImpressionsTracking/buildCardImpressionTrackingData';
import { getOrCreatePulse } from '@tools/tracking/utils';
import { captureException } from '@sentry/nextjs';
import { getRankFor } from '@shared/helpers/Rank';
import { useTrackImpression } from '@tools/tracking/useTrackImpression';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';
import { useAppSelector } from '@reducers/common';

import '@sbt-web/item-card/dist/index.css';

type Props = {
  index: number;
  item: AdItemType;
  listingCategory: CategoryID;
  handleItemClick?: () => void;
  enableOverrideUrgentForPro: boolean;
  shouldHideProDetails?: boolean;
};

export const shouldShowBigCards = (category: CategoryID) =>
  category === CategoryID.Auto ||
  getMacroOfCategoryID(category).id === CategoryID.Immobili;

export const AdItem: FC<Props> = ({
  index,
  item,
  listingCategory,
  handleItemClick,
  enableOverrideUrgentForPro,
  shouldHideProDetails = false,
}) => {
  const searchingInDialog = useAppSelector(
    ({ search }) => search.filtersDialogOpen || search.radiusDialogOpen
  );

  const searchId = useAppSelector(({ search }) => search.id);

  const rank = useAppSelector(({ items, search }: RootState) =>
    getRankFor(
      item.urn,
      item.gallerized ? 'GalleryItem' : 'AdItem',
      search,
      items
    )
  );

  const search = searchId ? { id: searchId, rank } : undefined;

  const { optimizely } = useContext(OptimizelySubitoContext);

  const bigCard = shouldShowBigCards(listingCategory);

  const itemCardRef = useRef(null);

  const setItemCardRef = useTrackImpression({
    enabled: search?.id !== undefined && !searchingInDialog,
    track: () => {
      getOrCreatePulse()?.queueEvent(
        buildCardImpressionTrackingViewData(getTrackingItemLocal())
      );
    },
  });

  const getTrackingItemLocal = () =>
    getTrackingItem(
      item,
      item.gallerized ? 'gallery' : 'listing',
      search?.id ?? '',
      search?.rank ?? -1
    );

  useEffect(() => {
    if (itemCardRef?.current) {
      // Set target to be observed by the IntersectionObserver
      setItemCardRef(itemCardRef.current);
    }
  }, [setItemCardRef, itemCardRef]);

  // A temporary fix to overwrite the name coming from Anubi with the name we
  // have locally: there is an encoding issue on non-ASCII characters.
  try {
    if (item.features[FeatureURI.QualitySeal] !== undefined) {
      const qsId = item.features[FeatureURI.QualitySeal].values[0].key;

      const localName = qualitySeals[qsId]?.name;

      if (localName !== undefined) {
        item.features[FeatureURI.QualitySeal].values[0].value = localName;
      }
    }
  } catch {
    captureException(
      new Error('Error while trying to overwrite the name of the quality seal')
    );
  }

  return (
    <ItemCard
      item={item}
      type={bigCard ? 'big' : 'small'}
      apiBase={HADES_PATH}
      impresapiuBase={process.env.NEXT_PUBLIC_IMPRESAPIU_BASE_URL}
      assetsBase={process.env.NEXT_PUBLIC_ASSETS_BASE_URL}
      search={search}
      lazyLoad={bigCard ? index >= 2 : index >= 4}
      ref={itemCardRef}
      pulseInstance={getOrCreatePulse()}
      onClick={() => {
        getOrCreatePulse()?.queueEvent(
          buildCardClickTrackingData(getTrackingItemLocal())
        );
        handleItemClick?.();
      }}
      houstonTrackConversion={(eventName) => {
        if (
          eventName === 'phone_number_viewed' &&
          item.category.id === CategoryID.Auto
        ) {
          optimizely?.onReady().then(() => {
            optimizely.track('lead_auto_pro');
          });
        }
      }}
      hideProDetails={shouldHideProDetails}
      overrideUrgentLabelForPro={enableOverrideUrgentForPro}
      pageTypeForTracking="listing"
    />
  );
};
